import { ParsedUrlQuery } from 'querystring';

import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Modal, ModalProps } from '../../../design-system/components/modal/Modal';
import { UseModalState } from '../../../design-system/components/modal/useModal';
import { ItinerariesListAvailableFilters } from '../../../resources/itinerary/domain/types/Itinerary';
import { PickDomain } from '../../../shared/utilities/types/PickDomain';

import { CheckboxesFilters } from './CheckboxesFilters';
import style from './FiltersModal.module.css';
import { PriceFilter } from './PriceFilter';

type FiltersModalProps = {
    modalProps: UseModalState;
    size: ModalProps['size'];
    filters: ParsedUrlQuery;
    availableFilters: PickDomain<ItinerariesListAvailableFilters, 'filters'>['filters'];
    onFilter: (filters: ParsedUrlQuery) => void;
};

const PRICE_FILTER_POSITION = 3;

export const FiltersModal = ({
    filters,
    modalProps,
    size,
    availableFilters,
    onFilter,
}: FiltersModalProps) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const [localFilters, setLocalFilters] = useState<ParsedUrlQuery>({ ...filters });
    const priceFilter = availableFilters.find((filter) => {
        return filter.name === 'price';
    });
    const checkboxes = availableFilters.filter((filter) => {
        return filter.type === 'choices' && filter.choices.length > 0;
    });

    useEffect(() => {
        setLocalFilters({ ...filters });
    }, [filters]);

    const handleValidateFilters = useCallback(async () => {
        onFilter(localFilters);
        modalProps.close();
    }, [localFilters, onFilter, modalProps]);

    const handleCloseModal = () => {
        modalProps.close();
        trackEvent((ampli, defaultProperties) => {
            ampli.listItinerariesFilterModalClosed({
                ...defaultProperties,
            });
        });
    };

    return (
        <Modal
            {...modalProps}
            close={handleCloseModal}
            size={size}
            title={formatMessage({ id: 'page.itinerariesList.filters.modal.title' })}
            primaryButton={{
                label: formatMessage({
                    id: 'page.itinerariesList.filters.modal.show.results',
                }),
                onClick: handleValidateFilters,
            }}
            secondaryButton={{
                label: formatMessage({ id: 'page.itinerariesList.filters.modal.clear' }),
                onClick: () => {
                    setLocalFilters({});
                },
            }}
        >
            <div className={style.filtersContainer}>
                <CheckboxesFilters
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                    availableFilters={checkboxes.slice(0, PRICE_FILTER_POSITION - 1)}
                />

                {priceFilter ? (
                    <PriceFilter
                        priceFilter={priceFilter}
                        handleFilters={(updatedQueryParams) => {
                            setLocalFilters((prevFilters) => ({
                                ...prevFilters,
                                ...updatedQueryParams,
                            }));
                        }}
                    />
                ) : null}

                <CheckboxesFilters
                    localFilters={localFilters}
                    setLocalFilters={setLocalFilters}
                    availableFilters={checkboxes.slice(
                        PRICE_FILTER_POSITION - 1,
                        checkboxes.length,
                    )}
                />
            </div>
        </Modal>
    );
};
